import { toast } from '@neo4j-ndl/react';
import { MODAL_TYPE, handleConnectionAbort, handleConnectionReady, useModal } from '@nx/state';
import type { CredentialsType } from '@nx/state/desktop';
import { useSetCredentialsMutation } from '@nx/state/desktop';
import * as StdLib from '@nx/stdlib';
import { isNotNullish, isNullish } from '@nx/stdlib';

export type DbmsConnectionInfo = {
  id: string;
  name: string;
  connectionUri: string;
  dbmsIsRunning: boolean;
  credentialsType: CredentialsType;
};

export const useConnectionFormModal = ({
  connectInfo,
  updateCredentialsOnSuccess,
  onConnectionReady,
}: {
  connectInfo: DbmsConnectionInfo;
  updateCredentialsOnSuccess: boolean;
  onConnectionReady?: () => void;
}) => {
  const modal = useModal(MODAL_TYPE.CONNECTION_FORM);
  const [setInstanceCredentials] = useSetCredentialsMutation();

  return {
    ...modal,
    close() {
      if (updateCredentialsOnSuccess) {
        handleConnectionAbort();
      }
      modal.close();
    },

    open({
      prefilledUsername,
      error,
      warning,
    }: {
      prefilledUsername?: string;
      error?: string;
      warning?: string;
    } = {}) {
      const neo4jUrl = StdLib.URLs.Neo4jURL.asNullable(connectInfo.connectionUri);

      if (isNullish(neo4jUrl)) {
        toast.danger('Instance connection URL is not valid, ensure that the URL is correct and try again.', {
          isCloseable: true,
        });
      } else {
        modal.open({
          instanceDetails: {
            id: connectInfo.id,
            name: connectInfo.name,
          },
          initialValues: {
            username: prefilledUsername ?? '',
            protocol: neo4jUrl.protocol,
            hostname: neo4jUrl.host,
          },
          hideConnectionMethods: true,
          source: { type: 'desktop', sourceId: connectInfo.id },
          error,
          warning,
        });
      }

      handleConnectionReady(({ driverConnectInfo }) => {
        if (StdLib.isNotNullish(onConnectionReady)) {
          onConnectionReady();
        }

        if (updateCredentialsOnSuccess) {
          if (driverConnectInfo?.authToken.scheme !== 'basic') {
            // If the user connected through SSO, or for some reason
            // driverConnectInfo is undefined we shouldn't save the credentials.
            return;
          }

          const username = driverConnectInfo.authToken.principal;
          const password = driverConnectInfo.authToken.credentials;
          if (isNotNullish(username) && isNotNullish(password)) {
            void setInstanceCredentials({
              id: connectInfo.id,
              type: connectInfo.credentialsType,
              username,
              password,
            });
          }
        }
      });
    },
  };
};
